<template>
  <AppLayout>
    <div class="p-4 md:p-6">

      <!-- Header (desktop only) -->
      <div class="hidden md:flex items-center justify-between mb-2">
        <div>
          <h1 class="text-2xl font-bold text-slate-800 dark:text-white">Customers</h1>
          <p class="text-slate-500 dark:text-slate-400 text-sm mt-0.5">
            <span v-if="dataLoading" class="inline-block w-28 h-3 bg-slate-200 dark:bg-slate-700 rounded-full animate-pulse"></span>
          <span v-else>{{ filtered.length }} of {{ customers.length }} customer{{ customers.length !== 1 ? 's' : '' }}</span>
          </p>
        </div>
        <button @click="openAdd"
          class="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-400 active:scale-95 text-white
                 text-sm font-semibold px-4 py-2.5 rounded-xl shadow-sm shadow-indigo-500/30 transition">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
          </svg>
          <span class="hidden sm:inline">Add Customer</span>
          <span class="sm:hidden">Add</span>
        </button>
      </div>

      <!-- ── MOBILE HEADER + STATS + SEARCH ── -->
      <div class="md:hidden -mx-4 -mt-4 mb-1">
        <!-- Indigo gradient hero -->
        <div class="bg-gradient-to-br from-indigo-600 via-indigo-500 to-violet-500 px-4 pt-3 pb-6">
          <div class="flex items-center justify-between mb-3">
            <div>
              <h1 class="text-xl font-bold text-white">Customers</h1>
              <p class="text-indigo-200 text-xs mt-0.5">
                <span v-if="dataLoading">Loading...</span>
                <span v-else>{{ filtered.length }} of {{ customers.length }} customer{{ customers.length !== 1 ? 's' : '' }}</span>
              </p>
            </div>
            <button @click="openAdd"
              class="flex items-center gap-1.5 bg-white/20 active:bg-white/30 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
              </svg>
              Add
            </button>
          </div>
          <!-- Stats row -->
          <div v-if="!dataLoading" class="grid grid-cols-3 gap-2 mb-2">
            <div class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
              <p class="text-[9px] font-bold text-emerald-200 uppercase tracking-widest mb-1">Receivable</p>
              <p class="text-[10px] min-[360px]:text-xs min-[400px]:text-sm font-bold text-white whitespace-nowrap">Rs {{ formatNum(totalReceivable) }}</p>
            </div>
            <div class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
              <p class="text-[9px] font-bold text-red-300 uppercase tracking-widest mb-1">Payable</p>
              <p class="text-[10px] min-[360px]:text-xs min-[400px]:text-sm font-bold text-white whitespace-nowrap">Rs {{ formatNum(totalPayable) }}</p>
            </div>
            <div class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
              <p class="text-[9px] font-bold uppercase tracking-widest mb-1"
                :class="totalBalance >= 0 ? 'text-yellow-200' : 'text-orange-300'">Net</p>
              <p class="text-[10px] min-[360px]:text-xs min-[400px]:text-sm font-bold text-white whitespace-nowrap">Rs {{ formatNum(Math.abs(totalBalance)) }}</p>
            </div>
          </div>
          <!-- Stats skeleton -->
          <div v-else class="grid grid-cols-3 gap-2 mb-2">
            <div v-for="i in 3" :key="i" class="bg-white/15 rounded-2xl h-14 animate-pulse"></div>
          </div>
        </div>

        <!-- Search card pulls up over hero -->
        <div class="bg-gray-50 dark:bg-slate-950 rounded-t-3xl -mt-4 px-4 pt-3 pb-2">
          <!-- Search input -->
          <div class="relative mb-2">
            <svg class="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none"
              fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607z"/>
            </svg>
            <input v-model="search" type="text" placeholder="Search customers..."
              class="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                     text-slate-800 dark:text-white placeholder-slate-400 rounded-2xl pl-10 pr-10 py-3 text-sm
                     focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm transition" />
            <button v-if="search" @click="search = ''"
              class="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
          <!-- Compact filter bar -->
          <div class="flex items-center gap-2">
            <!-- Balance pills -->
            <button v-for="opt in [{v:'',l:'All'},{v:'receivable',l:'Receivable'},{v:'payable',l:'Payable'}]" :key="opt.v"
              @click="balanceFilter = opt.v"
              class="shrink-0 text-xs font-semibold px-3.5 py-2 rounded-full transition"
              :class="balanceFilter === opt.v ? 'bg-indigo-500 text-white shadow-sm' : 'bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700'">
              {{ opt.l }}
            </button>

            <div class="flex-1"></div>

            <!-- Filters button with badge -->
            <button @click="mobileCityFiltersSheetOpen = true"
              class="relative shrink-0 flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-full transition"
              :class="selectedCity ? 'bg-indigo-500 text-white shadow-sm' : 'bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700'">
              <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75"/>
              </svg>
              Filters
              <span v-if="selectedCity"
                class="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-white text-indigo-600 text-[10px] font-bold flex items-center justify-center shadow">
                1
              </span>
            </button>
          </div>
        </div>
      </div>

      <!-- Search + Stats (desktop only) -->
      <div class="hidden md:flex items-center gap-3 mb-3">

        <!-- Search -->
        <div class="relative w-56">
          <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607z"/>
          </svg>
          <input v-model="search" type="text" placeholder="Search..."
            class="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                   text-slate-800 dark:text-white placeholder-slate-400 rounded-xl pl-9 pr-8 py-1.5 text-sm
                   focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition" />
          <button v-if="search" @click="search = ''"
            class="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-500 dark:text-slate-300 transition">
            <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- City filter -->
        <div class="relative">
          <select v-model="selectedCity"
            class="appearance-none bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                   text-slate-700 dark:text-slate-500 dark:text-slate-300 rounded-xl pl-3 pr-7 py-2 text-sm
                   focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition cursor-pointer">
            <option value="">All Cities</option>
            <option v-for="city in cityNames" :key="city" :value="city">{{ city }}</option>
          </select>
          <svg class="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
          </svg>
        </div>

        <!-- Balance filter dropdown -->
        <div class="relative">
          <select v-model="balanceFilter"
            class="appearance-none bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                   text-slate-700 dark:text-slate-300 rounded-xl pl-3 pr-7 py-2 text-sm
                   focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition cursor-pointer">
            <option value="">All Customers</option>
            <option value="receivable">Receivable</option>
            <option value="payable">Payable</option>
          </select>
          <svg class="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
          </svg>
        </div>

        <!-- Spacer -->
        <div class="flex-1"></div>

        <!-- Stats -->
        <div class="hidden md:flex items-center gap-2">
          <template v-if="dataLoading">
            <div class="h-10 w-28 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse"></div>
            <div class="h-10 w-28 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse"></div>
            <div class="h-10 w-28 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse"></div>
          </template>
          <template v-else>
          <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-xl px-4 py-2 text-right">
            <p class="text-[10px] font-semibold text-emerald-500 uppercase tracking-wide">Receivable</p>
            <p class="text-sm font-bold text-emerald-600 dark:text-emerald-400">Rs {{ formatNum(totalReceivable) }}</p>
          </div>
          <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-xl px-4 py-2 text-right">
            <p class="text-[10px] font-semibold text-red-400 uppercase tracking-wide">Payable</p>
            <p class="text-sm font-bold text-red-500 dark:text-red-400">Rs {{ formatNum(totalPayable) }}</p>
          </div>
          <div class="rounded-xl px-4 py-2 text-right"
            :class="totalBalance >= 0
              ? 'bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-100 dark:border-emerald-900'
              : 'bg-red-50 dark:bg-red-950/40 border border-red-100 dark:border-red-900'">
            <p class="text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap"
              :class="totalBalance >= 0 ? 'text-emerald-500' : 'text-red-400'">Net Balance</p>
            <p class="text-sm font-bold"
              :class="totalBalance >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'">
              Rs {{ formatNum(Math.abs(totalBalance)) }}
            </p>
          </div>
          </template>
        </div>
      </div>

      <!-- Flash -->
      <div v-if="$page.props.flash?.notice"
        class="mb-4 flex items-center gap-2 bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-400 text-sm px-4 py-3 rounded-xl">
        <svg class="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        {{ $page.props.flash.notice }}
      </div>
      <div v-if="$page.props.flash?.alert || errors?.length"
        class="mb-4 bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 text-sm px-4 py-3 rounded-xl">
        <p v-if="$page.props.flash?.alert">• {{ $page.props.flash.alert }}</p>
        <p v-for="e in (errors || [])" :key="e">• {{ e }}</p>
      </div>

      <!-- Skeleton -->
      <template v-if="dataLoading">
        <!-- Desktop skeleton -->
        <div class="hidden md:block bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden">
          <div class="border-b border-slate-100 dark:border-slate-800 px-5 py-3.5 flex gap-8">
            <div v-for="i in 5" :key="i" class="h-2.5 w-20 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
          </div>
          <div v-for="i in 7" :key="i" class="flex items-center gap-6 px-5 py-4 border-b border-slate-50 dark:border-slate-800/50">
            <div class="h-8 w-8 rounded-full bg-slate-100 dark:bg-slate-800 animate-pulse shrink-0"></div>
            <div class="flex-1 space-y-1.5">
              <div class="h-3 w-32 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
              <div class="h-2.5 w-24 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
            </div>
            <div class="h-3 w-20 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse ml-auto"></div>
            <div class="h-3 w-20 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
            <div class="h-3 w-20 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
            <div class="h-7 w-14 bg-slate-100 dark:bg-slate-800 rounded-lg animate-pulse"></div>
          </div>
        </div>
        <!-- Mobile skeleton -->
        <div class="md:hidden space-y-2.5">
          <div v-for="i in 5" :key="i"
            class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden">
            <div class="flex items-center gap-3 px-4 py-3.5">
              <div class="w-11 h-11 rounded-full bg-slate-100 dark:bg-slate-800 animate-pulse shrink-0"></div>
              <div class="flex-1 space-y-2">
                <div class="h-3 w-32 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
                <div class="h-2.5 w-24 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
              </div>
              <div class="h-8 w-16 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse shrink-0"></div>
            </div>
            <div class="grid grid-cols-3 divide-x divide-slate-100 dark:divide-slate-800 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/30">
              <div v-for="j in 3" :key="j" class="py-3 flex flex-col items-center gap-1.5">
                <div class="h-2 w-12 bg-slate-200 dark:bg-slate-700 rounded-full animate-pulse"></div>
                <div class="h-3 w-16 bg-slate-200 dark:bg-slate-700 rounded-full animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Empty state (desktop only) -->
      <div v-else-if="filtered.length === 0" class="hidden md:flex flex-col items-center justify-center py-20 text-center">
        <div class="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
          <svg class="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"/>
          </svg>
        </div>
        <p class="text-slate-600 dark:text-slate-400 font-medium">
          {{ (search || selectedCity || balanceFilter) ? 'No customers match your filters' : 'No customers yet' }}
        </p>
        <p class="text-slate-400 dark:text-slate-500 text-sm mt-1">
          {{ (search || selectedCity || balanceFilter) ? 'Try a different keyword or city' : 'Add your first customer to get started' }}
        </p>
        <button v-if="!(search || selectedCity || balanceFilter)" @click="openAdd" class="mt-4 text-indigo-500 text-sm font-semibold hover:text-indigo-400 transition">+ Add Customer</button>
        <button v-else @click="search = ''; selectedCity = ''; balanceFilter = ''" class="mt-4 text-indigo-500 text-sm font-semibold hover:text-indigo-400 transition">Clear filters</button>
      </div>

      <!-- Desktop table -->
      <div v-else class="hidden md:block bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-visible">
        <table class="w-full text-sm border-collapse" style="border-radius: inherit;">
          <thead>
            <tr class="border-b border-slate-100 dark:border-slate-800">
              <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide w-12">#</th>
              <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Customer</th>
              <th @click="toggleSort('receivable')" class="px-5 py-3.5 w-36 cursor-pointer select-none">
                <div class="flex items-center justify-end gap-1 text-xs font-semibold uppercase tracking-wide"
                  :class="sortCol === 'receivable' ? 'text-emerald-500' : 'text-emerald-500/60 hover:text-emerald-500'">
                  Receivable
                  <svg @mouseenter="showTooltip($event, 'receivable')" @mouseleave="hideTooltip" @click.stop
                    class="w-3.5 h-3.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition cursor-default"
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"/>
                  </svg>
                  <svg class="w-3 h-3 transition-transform" :class="sortCol === 'receivable' && sortDir === 'asc' ? 'rotate-180' : ''"
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
                  </svg>
                </div>
              </th>
              <th @click="toggleSort('payable')" class="px-5 py-3.5 w-36 cursor-pointer select-none">
                <div class="flex items-center justify-end gap-1 text-xs font-semibold uppercase tracking-wide"
                  :class="sortCol === 'payable' ? 'text-red-400' : 'text-red-400/60 hover:text-red-400'">
                  Payable
                  <svg @mouseenter="showTooltip($event, 'payable')" @mouseleave="hideTooltip" @click.stop
                    class="w-3.5 h-3.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition cursor-default"
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"/>
                  </svg>
                  <svg class="w-3 h-3 transition-transform" :class="sortCol === 'payable' && sortDir === 'asc' ? 'rotate-180' : ''"
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
                  </svg>
                </div>
              </th>
              <th @click="toggleSort('balance')" class="px-5 py-3.5 w-36 cursor-pointer select-none">
                <div class="flex items-center justify-end gap-1 text-xs font-semibold uppercase tracking-wide"
                  :class="sortCol === 'balance' ? 'text-slate-600 dark:text-slate-300' : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'">
                  Net Balance
                  <svg @mouseenter="showTooltip($event, 'balance')" @mouseleave="hideTooltip" @click.stop
                    class="w-3.5 h-3.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition cursor-default"
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"/>
                  </svg>
                  <svg class="w-3 h-3 transition-transform" :class="sortCol === 'balance' && sortDir === 'asc' ? 'rotate-180' : ''"
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
                  </svg>
                </div>
              </th>
              <th class="text-center px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide w-28">Actions</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(c, i) in filtered" :key="c.id">
              <!-- Main row -->
              <tr @click="toggleExpand(c.id)"
                class="border-b border-slate-50 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition cursor-pointer"
                :class="expandedId === c.id ? 'bg-slate-50 dark:bg-slate-800/50' : ''">
                <td class="px-5 py-4 text-slate-400 text-xs">{{ i + 1 }}</td>
                <td class="px-5 py-4">
                  <div class="flex items-center gap-3">
                    <div class="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center text-indigo-600 dark:text-indigo-400 text-sm font-bold shrink-0">
                      {{ c.name.charAt(0).toUpperCase() }}
                    </div>
                    <div>
                      <div class="flex items-center gap-1.5">
                        <span class="font-semibold text-slate-800 dark:text-white">{{ c.name }}</span>
                        <span v-if="c.public"
                          @mouseenter="showTooltip($event, 'public')" @mouseleave="hideTooltip"
                          class="inline-flex items-center justify-center w-4 h-4 rounded-full bg-emerald-100 dark:bg-emerald-900 text-emerald-500 shrink-0 cursor-default">
                          <svg class="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zm0 0c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3s4.5 4.03 4.5 9-2.015 9-4.5 9zm-9-9h18"/>
                          </svg>
                        </span>
                      </div>
                      <p class="text-xs text-slate-400 mt-0.5">{{ c.shop_name }}</p>
                    </div>
                  </div>
                </td>
                <td class="px-5 py-4 text-right">
                  <span class="font-semibold text-emerald-600 dark:text-emerald-400">Rs {{ formatNum(c.receivable) }}</span>
                </td>
                <td class="px-5 py-4 text-right">
                  <span class="font-semibold text-red-500 dark:text-red-400">Rs {{ formatNum(c.payable) }}</span>
                </td>
                <td class="px-5 py-4 text-right">
                  <span class="font-semibold" :class="(c.receivable - c.payable) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'">
                    Rs {{ formatNum(Math.abs(c.receivable - c.payable)) }}
                  </span>
                </td>
                <td class="px-5 py-4 text-center" @click.stop>
                  <div class="relative inline-block">
                    <button @click.stop="toggleActionDropdown(c.id)"
                      class="inline-flex items-center justify-center w-8 h-8 text-slate-500 dark:text-slate-400
                             hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition">
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                      </svg>
                    </button>
                    <!-- Dropdown menu -->
                    <div v-if="actionDropdownId === c.id" @click.stop
                      class="absolute right-0 bottom-full mb-1 w-40 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 z-50 overflow-hidden">
                      <button @click="openEdit(c); actionDropdownId = null"
                        class="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300
                               hover:bg-slate-50 dark:hover:bg-slate-700 transition">
                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                          <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z"/>
                        </svg>
                        Edit
                      </button>
                      <button @click="openDeleteConfirm(c); actionDropdownId = null"
                        class="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 dark:text-red-400
                               hover:bg-red-50 dark:hover:bg-red-950 transition border-t border-slate-100 dark:border-slate-700">
                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                          <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"/>
                        </svg>
                        Delete
                      </button>
                    </div>
                  </div>
                </td>
              </tr>

              <!-- Expanded detail row -->
              <tr v-if="expandedId === c.id" class="border-b border-slate-100 dark:border-slate-800">
                <td colspan="6" class="px-5 pt-1 pb-4 bg-slate-50/60 dark:bg-slate-800/30">
                  <div class="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
                    <div class="grid grid-cols-3 divide-x divide-slate-100 dark:divide-slate-800">

                      <!-- Panel 1: Contact -->
                      <div class="p-5">
                        <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4">Contact</p>
                        <div class="space-y-3.5">
                          <div class="flex items-center gap-3">
                            <div class="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
                              <svg class="w-4 h-4 text-slate-500 dark:text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"/>
                              </svg>
                            </div>
                            <div>
                              <p class="text-[10px] text-slate-400 leading-none mb-0.5">Phone</p>
                              <p class="text-sm font-semibold text-slate-700 dark:text-slate-200">{{ c.phone_number || '—' }}</p>
                            </div>
                          </div>
                          <div class="flex items-center gap-3">
                            <div class="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
                              <svg class="w-4 h-4 text-slate-500 dark:text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"/>
                                <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"/>
                              </svg>
                            </div>
                            <div>
                              <p class="text-[10px] text-slate-400 leading-none mb-0.5">City</p>
                              <p class="text-sm font-semibold text-slate-700 dark:text-slate-200">{{ c.city_name || '—' }}</p>
                            </div>
                          </div>
                          <div class="flex items-start gap-3">
                            <div class="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0 mt-0.5">
                              <svg class="w-4 h-4 text-slate-500 dark:text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"/>
                              </svg>
                            </div>
                            <div>
                              <p class="text-[10px] text-slate-400 leading-none mb-0.5">Address</p>
                              <p class="text-sm font-semibold text-slate-700 dark:text-slate-200">{{ c.address || '—' }}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Panel 2: Financials -->
                      <div class="p-5">
                        <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4">Financials</p>
                        <div class="space-y-2.5">
                          <div class="flex items-center justify-between bg-emerald-50 dark:bg-emerald-950/30 rounded-xl px-4 py-3 border border-emerald-100 dark:border-emerald-900/50">
                            <div class="flex items-center gap-2">
                              <span class="w-2 h-2 rounded-full bg-emerald-500 shrink-0"></span>
                              <p class="text-xs font-semibold text-emerald-700 dark:text-emerald-400">Receivable</p>
                            </div>
                            <p class="text-sm font-bold text-emerald-600 dark:text-emerald-400">Rs {{ formatNum(c.receivable) }}</p>
                          </div>
                          <div class="flex items-center justify-between bg-red-50 dark:bg-red-950/30 rounded-xl px-4 py-3 border border-red-100 dark:border-red-900/50">
                            <div class="flex items-center gap-2">
                              <span class="w-2 h-2 rounded-full bg-red-500 shrink-0"></span>
                              <p class="text-xs font-semibold text-red-600 dark:text-red-400">Payable</p>
                            </div>
                            <p class="text-sm font-bold text-red-500 dark:text-red-400">Rs {{ formatNum(c.payable) }}</p>
                          </div>
                          <div class="flex items-center justify-between rounded-xl px-4 py-3 border"
                            :class="(c.receivable - c.payable) >= 0
                              ? 'bg-indigo-50 dark:bg-indigo-950/30 border-indigo-100 dark:border-indigo-900/50'
                              : 'bg-orange-50 dark:bg-orange-950/30 border-orange-100 dark:border-orange-900/50'">
                            <div class="flex items-center gap-2">
                              <span class="w-2 h-2 rounded-full shrink-0"
                                :class="(c.receivable - c.payable) >= 0 ? 'bg-indigo-500' : 'bg-orange-500'"></span>
                              <p class="text-xs font-semibold"
                                :class="(c.receivable - c.payable) >= 0 ? 'text-indigo-600 dark:text-indigo-400' : 'text-orange-600 dark:text-orange-400'">
                                Net Balance
                              </p>
                            </div>
                            <p class="text-sm font-bold"
                              :class="(c.receivable - c.payable) >= 0 ? 'text-indigo-600 dark:text-indigo-400' : 'text-orange-600 dark:text-orange-400'">
                              Rs {{ formatNum(Math.abs(c.receivable - c.payable)) }}
                            </p>
                          </div>
                        </div>
                      </div>

                      <!-- Panel 3: Opening Balances + Visibility -->
                      <div class="p-5 flex flex-col justify-between">
                        <div>
                          <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4">Opening Balances</p>
                          <div class="space-y-1">
                            <div class="flex items-center justify-between py-2.5 border-b border-slate-100 dark:border-slate-800">
                              <p class="text-xs text-slate-500 dark:text-slate-400">To Receive</p>
                              <p class="text-sm font-semibold text-slate-700 dark:text-slate-200">Rs {{ formatNum(c.opening_balance_to_receive || 0) }}</p>
                            </div>
                            <div class="flex items-center justify-between py-2.5">
                              <p class="text-xs text-slate-500 dark:text-slate-400">To Give</p>
                              <p class="text-sm font-semibold text-slate-700 dark:text-slate-200">Rs {{ formatNum(c.opening_balance_to_give || 0) }}</p>
                            </div>
                          </div>
                        </div>
                        <div class="pt-4 mt-2 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                          <div>
                            <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Visibility</p>
                            <span class="text-xs font-semibold mt-0.5 block"
                              :class="c.public ? 'text-emerald-500' : 'text-slate-400'">
                              {{ c.public ? 'Public' : 'Private' }}
                            </span>
                          </div>
                          <div class="flex items-center gap-2">
                            <button @click.stop="togglePublic(c)" type="button"
                              class="relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none"
                              :class="c.public ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'">
                              <span class="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200"
                                :class="c.public ? 'translate-x-4' : 'translate-x-0'"></span>
                            </button>
                            <!-- Share dropdown -->
                            <div v-if="c.public" class="relative" @click.stop>
                              <button @click="toggleShareDropdown(c.id)"
                                class="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900">
                                <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                  <path stroke-linecap="round" stroke-linejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244"/>
                                </svg>
                                {{ copiedId === c.id ? 'Copied!' : 'Share' }}
                                <svg class="w-3 h-3 transition-transform" :class="shareDropdownId === c.id ? 'rotate-180' : ''" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                                  <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
                                </svg>
                              </button>
                              <div v-if="shareDropdownId === c.id"
                                class="absolute right-0 bottom-full mb-1.5 w-44 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-xl overflow-hidden z-50">
                                <button @click="copyLink(c.id); shareDropdownId = null"
                                  class="flex items-center gap-2.5 w-full px-3.5 py-2.5 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/60 transition">
                                  <svg class="w-3.5 h-3.5 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244"/>
                                  </svg>
                                  {{ copiedId === c.id ? 'Copied!' : 'Copy Link' }}
                                </button>
                                <a v-if="c.phone_number" :href="whatsappLink(c)" @click="shareDropdownId = null"
                                  class="flex items-center gap-2.5 w-full px-3.5 py-2.5 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/60 transition border-t border-slate-100 dark:border-slate-700">
                                  <svg class="w-3.5 h-3.5 text-green-500" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                  </svg>
                                  WhatsApp
                                </a>
                              </div>
                            </div>
                            <a v-if="c.public" :href="`/customers/${c.id}/details`" target="_blank" @click.stop
                              class="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900">
                              <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"/>
                              </svg>
                              Details
                            </a>
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- Mobile cards (native style) -->
      <div v-if="!dataLoading" class="md:hidden space-y-2.5">

        <!-- Empty state -->
        <div v-if="filtered.length === 0" class="flex flex-col items-center justify-center py-16 text-center">
          <div class="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
            <svg class="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"/>
            </svg>
          </div>
          <p class="text-slate-600 dark:text-slate-400 font-medium">
            {{ (search || selectedCity || balanceFilter) ? 'No customers match' : 'No customers yet' }}
          </p>
          <p class="text-slate-400 dark:text-slate-500 text-sm mt-1">
            {{ (search || selectedCity || balanceFilter) ? 'Try a different keyword or city' : 'Add your first customer to get started' }}
          </p>
          <button v-if="!(search || selectedCity || balanceFilter)" @click="openAdd" class="mt-4 text-indigo-500 text-sm font-semibold">+ Add Customer</button>
          <button v-else @click="search = ''; selectedCity = ''; balanceFilter = ''" class="mt-4 text-indigo-500 text-sm font-semibold">Clear filters</button>
        </div>

        <div v-for="c in filtered" :key="c.id"
          class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden">

          <!-- Card face (tap to expand) -->
          <div class="flex items-center gap-3 px-4 py-3.5 cursor-pointer active:bg-slate-50 dark:active:bg-slate-800/50 transition"
            @click="toggleExpand(c.id)">
            <!-- Colored avatar -->
            <div class="w-11 h-11 rounded-full flex items-center justify-center text-white text-base font-bold shrink-0"
              :style="{ background: avatarColor(c.name) }">
              {{ c.name.charAt(0).toUpperCase() }}
            </div>
            <!-- Name + shop -->
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-1.5">
                <p class="font-semibold text-slate-800 dark:text-white text-sm truncate">{{ c.name }}</p>
                <span v-if="c.public"
                  class="inline-flex items-center justify-center w-4 h-4 rounded-full bg-emerald-100 dark:bg-emerald-900 text-emerald-500 shrink-0">
                  <svg class="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zm0 0c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3s4.5 4.03 4.5 9-2.015 9-4.5 9zm-9-9h18"/>
                  </svg>
                </span>
              </div>
              <p class="text-xs text-slate-400 mt-0.5 truncate">{{ c.shop_name }}</p>
            </div>
            <!-- Balance badge + chevron -->
            <div class="shrink-0 text-right mr-1">
              <p class="text-[10px] font-medium text-slate-400 mb-0.5">Balance</p>
              <p class="text-sm font-bold leading-none"
                :class="(c.receivable - c.payable) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'">
                Rs {{ formatNum(Math.abs(c.receivable - c.payable)) }}
              </p>
            </div>
            <svg class="w-4 h-4 text-slate-300 shrink-0 transition-transform duration-200"
              :class="expandedId === c.id ? 'rotate-180' : ''"
              fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
            </svg>
          </div>

          <!-- Expanded detail -->
          <div v-if="expandedId === c.id" class="border-t border-slate-100 dark:border-slate-800">

            <!-- Financial strip -->
            <div class="grid grid-cols-3 divide-x divide-slate-100 dark:divide-slate-800 bg-slate-50/80 dark:bg-slate-800/30">
              <div class="py-2.5 text-center">
                <p class="text-[9px] font-bold text-emerald-500 uppercase tracking-widest mb-0.5">Receivable</p>
                <p class="text-xs font-bold text-emerald-600 dark:text-emerald-400">Rs {{ formatNum(c.receivable) }}</p>
              </div>
              <div class="py-2.5 text-center">
                <p class="text-[9px] font-bold text-red-400 uppercase tracking-widest mb-0.5">Payable</p>
                <p class="text-xs font-bold text-red-500 dark:text-red-400">Rs {{ formatNum(c.payable) }}</p>
              </div>
              <div class="py-2.5 text-center">
                <p class="text-[9px] font-bold uppercase tracking-widest mb-0.5"
                  :class="(c.receivable - c.payable) >= 0 ? 'text-indigo-500' : 'text-orange-500'">Net</p>
                <p class="text-xs font-bold"
                  :class="(c.receivable - c.payable) >= 0 ? 'text-indigo-600 dark:text-indigo-400' : 'text-orange-500 dark:text-orange-400'">
                  Rs {{ formatNum(Math.abs(c.receivable - c.payable)) }}
                </p>
              </div>
            </div>

            <!-- Contact info -->
            <div class="px-4 pt-3.5 pb-1 bg-white dark:bg-slate-900">
              <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2.5">Contact Info</p>
              <div class="space-y-2.5">
                <!-- Phone & City in single row -->
                <div class="grid grid-cols-2 gap-2">
                  <div class="flex items-center gap-2.5">
                    <div class="w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
                      <svg class="w-3.5 h-3.5 text-slate-500 dark:text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"/>
                      </svg>
                    </div>
                    <div class="min-w-0">
                      <p class="text-[10px] text-slate-400 leading-none">Phone</p>
                      <p class="text-xs font-semibold text-slate-700 dark:text-slate-200 mt-0.5 truncate">{{ c.phone_number || '—' }}</p>
                    </div>
                  </div>
                  <div class="flex items-center gap-2.5">
                    <div class="w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
                      <svg class="w-3.5 h-3.5 text-slate-500 dark:text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"/>
                      </svg>
                    </div>
                    <div class="min-w-0">
                      <p class="text-[10px] text-slate-400 leading-none">City</p>
                      <p class="text-xs font-semibold text-slate-700 dark:text-slate-200 mt-0.5 truncate">{{ c.city_name || '—' }}</p>
                    </div>
                  </div>
                </div>
                <!-- Address -->
                <div v-if="c.address" class="flex items-start gap-2.5">
                  <div class="w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0 mt-0.5">
                    <svg class="w-3.5 h-3.5 text-slate-500 dark:text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"/>
                    </svg>
                  </div>
                  <div>
                    <p class="text-[10px] text-slate-400 leading-none">Address</p>
                    <p class="text-xs font-semibold text-slate-700 dark:text-slate-200 mt-0.5">{{ c.address }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Opening Balances -->
            <div class="px-4 pt-3 pb-4 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800">
              <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2.5">Opening Balances</p>
              <div class="grid grid-cols-2 gap-2">
                <div class="bg-slate-50 dark:bg-slate-800 rounded-xl p-3">
                  <p class="text-[10px] text-slate-400 mb-0.5">To Receive</p>
                  <p class="text-sm font-bold text-slate-700 dark:text-slate-200">Rs {{ formatNum(c.opening_balance_to_receive || 0) }}</p>
                </div>
                <div class="bg-slate-50 dark:bg-slate-800 rounded-xl p-3">
                  <p class="text-[10px] text-slate-400 mb-0.5">To Give</p>
                  <p class="text-sm font-bold text-slate-700 dark:text-slate-200">Rs {{ formatNum(c.opening_balance_to_give || 0) }}</p>
                </div>
              </div>
            </div>

            <!-- Action row -->
            <div class="bg-white dark:bg-slate-900 space-y-0">
              <!-- Edit & Delete buttons -->
              <div class="grid grid-cols-2 gap-2 px-4 pt-3 pb-3">
                <button @click.stop="openEdit(c)"
                  class="flex items-center justify-center gap-2 text-sm font-semibold text-indigo-600 dark:text-indigo-400
                         bg-white dark:bg-slate-900 border border-indigo-200 dark:border-indigo-800 rounded-xl py-2.5 transition active:scale-95">
                  <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z"/>
                  </svg>
                  Edit
                </button>
                <button @click.stop="openDeleteConfirm(c)"
                  class="flex items-center justify-center gap-2 text-sm font-semibold text-red-600 dark:text-red-400
                         bg-white dark:bg-slate-900 border border-red-200 dark:border-red-800 rounded-xl py-2.5 transition active:scale-95">
                  <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"/>
                  </svg>
                  Delete
                </button>
              </div>

              <!-- Public / Private toggle row -->
              <div class="mx-4 mb-3 rounded-2xl border border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40 overflow-hidden">
                <button @click.stop="togglePublic(c)" type="button"
                  class="w-full flex items-center gap-3 px-4 py-3.5 transition active:bg-slate-100 dark:active:bg-slate-700/50">
                  <!-- Icon -->
                  <div class="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition"
                    :class="c.public ? 'bg-emerald-100 dark:bg-emerald-900/60' : 'bg-slate-200 dark:bg-slate-700'">
                    <svg v-if="c.public" class="w-4.5 h-4.5 w-[18px] text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zm0 0c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3s4.5 4.03 4.5 9-2.015 9-4.5 9zm-9-9h18"/>
                    </svg>
                    <svg v-else class="w-[18px] text-slate-500 dark:text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"/>
                    </svg>
                  </div>
                  <!-- Label -->
                  <div class="flex-1 text-left">
                    <p class="text-sm font-semibold transition"
                      :class="c.public ? 'text-emerald-700 dark:text-emerald-400' : 'text-slate-700 dark:text-slate-300'">
                      {{ c.public ? 'Public Profile' : 'Private Profile' }}
                    </p>
                    <p class="text-xs mt-0.5 transition"
                      :class="c.public ? 'text-emerald-600/70 dark:text-emerald-500/70' : 'text-slate-400 dark:text-slate-500'">
                      {{ c.public ? 'Shareable page is active' : 'Tap to make shareable' }}
                    </p>
                  </div>
                  <!-- Toggle switch -->
                  <div class="relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200"
                    :class="c.public ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'">
                    <span class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition duration-200"
                      :class="c.public ? 'translate-x-5' : 'translate-x-0'"></span>
                  </div>
                </button>

                <!-- Share options — only visible when public, expand inline -->
                <div v-if="c.public" class="border-t border-slate-100 dark:border-slate-700/60 px-4 py-3">
                  <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2.5">Share With Customer</p>
                  <div class="grid gap-2" :class="c.phone_number ? 'grid-cols-3' : 'grid-cols-2'">
                    <!-- Copy link -->
                    <button @click.stop="copyLink(c.id)"
                      class="flex flex-col items-center gap-1.5 py-2.5 rounded-xl transition active:scale-95"
                      :class="copiedId === c.id ? 'bg-indigo-500 text-white' : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300'">
                      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244"/>
                      </svg>
                      <span class="text-[10px] font-semibold">{{ copiedId === c.id ? 'Copied!' : 'Copy Link' }}</span>
                    </button>
                    <!-- WhatsApp -->
                    <a v-if="c.phone_number" :href="whatsappLink(c)" @click.stop
                      class="flex flex-col items-center gap-1.5 py-2.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 transition active:scale-95">
                      <svg class="w-4 h-4 text-green-500" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      <span class="text-[10px] font-semibold">WhatsApp</span>
                    </a>
                    <!-- Details -->
                    <a :href="`/customers/${c.id}/details`" @click.stop
                      class="flex flex-col items-center gap-1.5 py-2.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 transition active:scale-95">
                      <svg class="w-4 h-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"/>
                      </svg>
                      <span class="text-[10px] font-semibold">View Page</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

    </div>

    <!-- ── ADD / EDIT MODAL ── -->
    <Modal v-if="showModal" @close="closeModal">
      <template #title>{{ editTarget ? 'Edit Customer' : 'Add Customer' }}</template>
      <form @submit.prevent="submitForm" class="space-y-4">

        <div class="grid grid-cols-2 gap-3">
          <InputField v-model="form.name"      label="Full Name"  placeholder="Muhammad Aslam" required class="col-span-2" />
          <InputField v-model="form.shop_name" label="Business Name"  placeholder="Aslam Hardware" required class="col-span-2" />
          <InputField v-model="form.phone_number" label="Phone"   placeholder="03001234567" />
          <div>
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">City <span class="text-red-400">*</span></label>
            <div class="relative">
              <select v-model="form.city_id" required
                class="w-full appearance-none bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                       text-slate-800 dark:text-white rounded-xl px-4 pr-9 py-3 text-sm
                       focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition cursor-pointer">
                <option value="" disabled>Select city</option>
                <option v-for="city in cities" :key="city.id" :value="city.id">{{ city.name }}</option>
              </select>
              <svg class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400"
                fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
              </svg>
            </div>
          </div>
          <InputField v-model="form.address"   label="Address"    placeholder="Shop 5, Main Bazar" class="col-span-2" />
          <div class="col-span-2">
            <p class="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Opening Balances</p>
            <div class="grid grid-cols-2 gap-3">
              <InputField v-model="form.opening_balance_to_receive" label="To Receive" placeholder="0" type="number" step="1" />
              <InputField v-model="form.opening_balance_to_give"    label="To Give"    placeholder="0" type="number" step="1" />
            </div>
          </div>
        </div>

        <div class="flex gap-3 pt-2">
          <button type="button" @click="closeModal"
            class="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-medium
                   text-slate-600 dark:text-slate-500 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition">
            Cancel
          </button>
          <button type="submit" :disabled="loading"
            class="flex-1 py-2.5 rounded-xl bg-indigo-500 hover:bg-indigo-400 disabled:opacity-60 text-white text-sm font-semibold transition">
            {{ loading ? 'Saving...' : (editTarget ? 'Update' : 'Add Customer') }}
          </button>
        </div>
      </form>
    </Modal>

    <!-- ── DELETE CONFIRM DIALOG ── -->
    <ConfirmDialog
      v-if="showDeleteConfirm"
      title="Delete Customer?"
      :message="`Are you sure you want to delete '${deleteTarget?.name}'? This action cannot be undone.`"
      info-message="Deleting this customer won't affect existing invoices, payments, or reports. All historical data will remain intact and accessible."
      confirm-text="Yes, Delete"
      loading-text="Deleting..."
      variant="danger"
      @confirm="confirmDelete"
      @cancel="closeDeleteConfirm"
    />

    <!-- Tooltip -->
    <Teleport to="body">
      <div v-if="tooltip.visible" :style="{ top: tooltip.y + 'px', left: tooltip.x + 'px' }"
        class="fixed z-[9999] w-56 pointer-events-none"
        style="transform: translate(-50%, -100%)">
        <div class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs rounded-xl px-3 py-2.5 shadow-xl leading-relaxed">

          <!-- Receivable -->
          <template v-if="tooltip.type === 'receivable'">
            <p class="font-semibold mb-1.5 text-slate-700 dark:text-slate-200">Receivable Formula</p>
            <p class="text-slate-500 dark:text-slate-300">Opening Balance (To Receive)</p>
            <p class="text-slate-500 dark:text-slate-300">+ Sale Invoices Total</p>
            <p class="text-slate-500 dark:text-slate-300">− Received Payments</p>
            <div class="border-t border-slate-200 dark:border-slate-600 mt-1.5 pt-1.5 text-emerald-500 font-medium">= Amount customer owes you</div>
          </template>

          <!-- Payable -->
          <template v-else-if="tooltip.type === 'payable'">
            <p class="font-semibold mb-1.5 text-slate-700 dark:text-slate-200">Payable Formula</p>
            <p class="text-slate-500 dark:text-slate-300">Opening Balance (To Give)</p>
            <p class="text-slate-500 dark:text-slate-300">+ Purchase Invoices Total</p>
            <p class="text-slate-500 dark:text-slate-300">− Sent Payments</p>
            <div class="border-t border-slate-200 dark:border-slate-600 mt-1.5 pt-1.5 text-red-500 font-medium">= Amount you owe customer</div>
          </template>

          <!-- Public profile -->
          <template v-else-if="tooltip.type === 'public'">
            <p class="font-semibold mb-1 text-slate-700 dark:text-slate-200">Public Profile</p>
            <p class="text-slate-500 dark:text-slate-300">This customer has a shareable public page with their full transaction history, invoices and payments.</p>
          </template>

          <!-- Balance -->
          <template v-else-if="tooltip.type === 'balance'">
            <p class="font-semibold mb-1.5 text-slate-700 dark:text-slate-200">Balance Formula</p>
            <p class="text-slate-500 dark:text-slate-300">Receivable − Payable</p>
            <div class="border-t border-slate-200 dark:border-slate-600 mt-1.5 pt-1.5 font-medium text-slate-500 dark:text-slate-300">
              <span class="text-emerald-500">Green</span> = you are owed money<br>
              <span class="text-red-500">Red</span> = you owe money
            </div>
          </template>

        </div>
        <!-- Arrow -->
        <div class="w-2 h-2 bg-white dark:bg-slate-800 border-r border-b border-slate-200 dark:border-slate-700 rotate-45 mx-auto -mt-1"></div>
      </div>
    </Teleport>

    <!-- ── Mobile city filter bottom sheet ── -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition-opacity duration-300"
        enter-from-class="opacity-0" enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-200"
        leave-from-class="opacity-100" leave-to-class="opacity-0">
        <div v-if="mobileCityFiltersSheetOpen"
          class="md:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
          @click="mobileCityFiltersSheetOpen = false" />
      </Transition>
      <Transition
        enter-active-class="transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]"
        enter-from-class="translate-y-full" enter-to-class="translate-y-0"
        leave-active-class="transition-transform duration-200 ease-in"
        leave-from-class="translate-y-0" leave-to-class="translate-y-full">
        <div v-if="mobileCityFiltersSheetOpen"
          class="md:hidden fixed inset-x-0 bottom-0 z-50 bg-white dark:bg-slate-900 rounded-t-3xl shadow-2xl flex flex-col"
          style="max-height: 82vh"
          @click.stop>
          <!-- Handle -->
          <div class="flex justify-center pt-3 pb-1 shrink-0">
            <div class="w-10 h-1 rounded-full bg-slate-300 dark:bg-slate-600"></div>
          </div>
          <!-- Header -->
          <div class="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <p class="font-semibold text-slate-800 dark:text-white text-base">Filters</p>
            <div class="flex items-center gap-2">
              <button v-if="selectedCity" @click="selectedCity = ''; mobileCityFiltersSheetOpen = false"
                class="text-xs font-semibold text-red-500 px-3 py-1.5 rounded-full bg-red-50 dark:bg-red-950/50 transition">
                Clear all
              </button>
              <button @click="mobileCityFiltersSheetOpen = false"
                class="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
          </div>
          <!-- Content -->
          <div class="overflow-y-auto flex-1 px-4 py-4">
            <p class="text-xs font-bold text-slate-400 uppercase tracking-wide mb-3">City</p>
            <div class="flex flex-wrap gap-2">
              <button @click="selectedCity = ''"
                class="text-xs font-semibold px-3.5 py-2 rounded-full transition"
                :class="selectedCity === '' ? 'bg-indigo-500 text-white shadow-sm' : 'bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700'">
                All Cities
              </button>
              <button v-for="city in cityNames" :key="city" @click="selectedCity = city"
                class="text-xs font-semibold px-3.5 py-2 rounded-full transition"
                :class="selectedCity === city ? 'bg-indigo-500 text-white shadow-sm' : 'bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700'">
                {{ city }}
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

  </AppLayout>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useForm, usePage, router } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'
import Modal from '@/components/Modal.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import InputField from '@/components/InputField.vue'

const props = defineProps({
  cities: {
    type: Array,
    default: () => []
  }
})

const page  = usePage()
const errors = computed(() => page.props.errors)
const businessName = computed(() => page.props.auth?.user?.business_name?.trim() || 'Master Ledger')

const customers    = ref([])
const dataLoading  = ref(true)

// City names for filtering
const cityNames = computed(() => {
  const uniqueCities = new Set(customers.value.map(c => c.city_name).filter(Boolean))
  return Array.from(uniqueCities).sort()
})

async function fetchData() {
  dataLoading.value = true
  try {
    const res  = await fetch('/customers/data', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
    const json = await res.json()
    customers.value = json.customers || []
  } finally {
    dataLoading.value = false
  }
}

onMounted(() => {
  document.title = `Customers | ${businessName.value}`
  fetchData()
  document.addEventListener('click', closeShareDropdown)
  document.addEventListener('click', closeActionDropdown)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', closeShareDropdown)
  document.removeEventListener('click', closeActionDropdown)
})

// ── Tooltip ────────────────────────────────────────
const tooltip = ref({ visible: false, type: null, x: 0, y: 0 })

function showTooltip(event, type) {
  const rect = event.target.getBoundingClientRect()
  tooltip.value = {
    visible: true,
    type,
    x: rect.left + rect.width / 2 + window.scrollX,
    y: rect.top + window.scrollY,
  }
}

function hideTooltip() {
  tooltip.value.visible = false
}

// ── Row expand ─────────────────────────────────────
const expandedId = ref(null)
function toggleExpand(id) {
  expandedId.value = expandedId.value === id ? null : id
}

// ── Action dropdown ────────────────────────────────
const actionDropdownId = ref(null)
function toggleActionDropdown(id) {
  actionDropdownId.value = actionDropdownId.value === id ? null : id
}
function closeActionDropdown() {
  actionDropdownId.value = null
}

// ── Search ─────────────────────────────────────────
const search = ref('')

// ── City filter ────────────────────────────────────
const selectedCity   = ref('')

// ── Balance filter ──────────────────────────────────
const balanceFilter           = ref('')  // '' | 'receivable' | 'payable'
const mobileCityFiltersSheetOpen = ref(false)

// ── Sort ───────────────────────────────────────────
const sortCol = ref(null)   // 'receivable' | 'payable' | 'balance'
const sortDir = ref('desc') // 'asc' | 'desc'

function toggleSort(col) {
  if (sortCol.value === col) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortCol.value = col
    sortDir.value = 'desc'
  }
}

const filtered = computed(() => {
  const q = search.value.trim().toLowerCase()
  const list = customers.value.filter(c => {
    if (selectedCity.value && c.city_name !== selectedCity.value) return false
    if (balanceFilter.value === 'receivable' && !((c.receivable || 0) > 0)) return false
    if (balanceFilter.value === 'payable'    && !((c.payable    || 0) > 0)) return false
    if (q) {
      return c.name.toLowerCase().includes(q) ||
             c.shop_name.toLowerCase().includes(q) ||
             c.city_name.toLowerCase().includes(q) ||
             (c.phone_number || '').includes(q)
    }
    return true
  })

  if (!sortCol.value) return [...list].sort((a, b) => a.name.localeCompare(b.name))

  return [...list].sort((a, b) => {
    const val = (c) => {
      if (sortCol.value === 'receivable') return c.receivable || 0
      if (sortCol.value === 'payable')    return c.payable || 0
      return (c.receivable || 0) - (c.payable || 0) // balance
    }
    return sortDir.value === 'asc' ? val(a) - val(b) : val(b) - val(a)
  })
})

function formatNum(n) {
  return Math.floor(Number(n)).toLocaleString('en-PK')
}

const AVATAR_COLORS = ['#6366f1','#8b5cf6','#ec4899','#f59e0b','#10b981','#3b82f6','#ef4444','#14b8a6','#f97316','#84cc16']
function avatarColor(name) {
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

// ── Totals ─────────────────────────────────────────
const totalReceivable = computed(() => filtered.value.reduce((sum, c) => sum + (c.receivable || 0), 0))
const totalPayable    = computed(() => filtered.value.reduce((sum, c) => sum + (c.payable || 0), 0))
const totalBalance    = computed(() => totalReceivable.value - totalPayable.value)

// ── Toggle public ──────────────────────────────────
function togglePublic(c) {
  c.public = !c.public
  router.patch(`/customers/${c.id}/toggle_public`, {}, { preserveScroll: true })
}

// ── Share link ─────────────────────────────────────
const copiedId = ref(null)
const shareDropdownId = ref(null)
function closeShareDropdown() { shareDropdownId.value = null }
function toggleShareDropdown(id) { shareDropdownId.value = shareDropdownId.value === id ? null : id }
function copyLink(id) {
  const url = `${window.location.origin}/customers/${id}/details`
  navigator.clipboard.writeText(url)
  copiedId.value = id
  setTimeout(() => { copiedId.value = null }, 2000)
}

function whatsappLink(c) {
  const digits = (c.phone_number || '').replace(/\D/g, '')
  const intl = digits.startsWith('92') ? digits : digits.startsWith('0') ? '92' + digits.slice(1) : '92' + digits
  const url = `${window.location.origin}/customers/${c.id}/details`
  const firstName = c.name.split(' ')[0]
  const text = encodeURIComponent(`Aslamoalikum, ${firstName} sahib ye apki full account statement ha.\n${url}`)
  return `https://wa.me/${intl}?text=${text}`
}

// ── Modal state ────────────────────────────────────
const showModal  = ref(false)
const editTarget = ref(null)
const loading    = ref(false)

const emptyForm = () => ({ name: '', shop_name: '', phone_number: '', address: '', city_id: '', opening_balance_to_receive: 0, opening_balance_to_give: 0 })
const form = ref(emptyForm())

function openAdd() {
  editTarget.value = null
  form.value = emptyForm()
  showModal.value = true
}

function openEdit(c) {
  editTarget.value = c
  form.value = { name: c.name, shop_name: c.shop_name, phone_number: c.phone_number || '', address: c.address || '', city_id: c.city_id, opening_balance_to_receive: c.opening_balance_to_receive, opening_balance_to_give: c.opening_balance_to_give }
  showModal.value = true
}

function closeModal() {
  showModal.value = false
  editTarget.value = null
}

function submitForm() {
  loading.value = true
  const req = useForm({ customer: form.value })
  const opts = {
    onSuccess: () => { closeModal(); fetchData() },
    onFinish:  () => { loading.value = false },
  }

  if (editTarget.value) {
    req.patch(`/customers/${editTarget.value.id}`, opts)
  } else {
    req.post('/customers', opts)
  }
}

// ── Delete customer ────────────────────────────────
const showDeleteConfirm = ref(false)
const deleteTarget = ref(null)

function openDeleteConfirm(customer) {
  deleteTarget.value = customer
  showDeleteConfirm.value = true
}

function closeDeleteConfirm() {
  showDeleteConfirm.value = false
  deleteTarget.value = null
}

function confirmDelete() {
  return new Promise((resolve) => {
    router.delete(`/customers/${deleteTarget.value.id}`, {
      onSuccess: () => {
        closeDeleteConfirm()
        fetchData()
        resolve()
      },
      onError: () => {
        resolve()
      }
    })
  })
}
</script>
